/* ==========================================================================
   SS MOTION controller. 2026-09-02, RJ item 7.

   Jobs, in order of importance:
     1. The interlock. Adds `ss-motion` to <body> ONLY when the reader has
        not asked for reduced motion. Every hide-then-reveal rule in
        ss-motion.css sits under that class, so no script = nothing hidden.
     2. Scroll reveal. Tags the Spicy South components that should rise and
        unhides each as it is scrolled to, staggered inside its row and
        capped at six steps so a long grid never keeps a reader waiting.
     3. Count-up on the proof numbers (25 / 365) the first time they are
        seen. rAF with a setTimeout backstop and a document.hidden short
        circuit, so a background tab can never strand "17".
     4. Touch ripple from the pointer's own coordinates on tiles, cards and
        segmented controls.
     5. Cart icon bump plus a 12ms haptic tick on every cart:update.

   Fuel pages have fuel-motion.js for their own surfaces; nothing here
   selects a .fuel component. Vanilla, no build step, delegated listeners.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = false;
  try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  var RISE = [
    '.ss-states .ss-tile',
    '.ss-states .ss-pcard',
    '.ss-lines__lead > *',
    '.ss-lines__card',
    '.ss-proof__fact',
    '.product-grid__item'
  ];
  var STEP = 40;
  var MAX_STEPS = 4;
  var FORCE_MS = 900; /* anything not seen by then is shown anyway */

  var RIPPLE = '.ss-tile, .ss-pcard, .ss-lines__tab, .ss-lines__cta, .ss-rewards__apply, .ds-toggle';

  /* ---- 3. count-up ------------------------------------------------------ */
  function countUp(el) {
    var to = parseInt(el.getAttribute('data-ss-count'), 10);
    if (!to || el.getAttribute('data-counted')) return;
    el.setAttribute('data-counted', '1');
    if (reduce || document.hidden) { el.textContent = String(to); return; }
    var start = null;
    var dur = 720;
    var done = false;
    function land() { if (done) return; done = true; el.textContent = String(to); }
    var backstop = setTimeout(land, dur + 120);
    function step(t) {
      if (done) return;
      if (document.hidden) { clearTimeout(backstop); land(); return; }
      if (start === null) start = t;
      var p = Math.min(1, (t - start) / dur);
      var e = 1 - Math.pow(1 - p, 3);
      el.textContent = String(Math.round(to * e));
      if (p < 1) requestAnimationFrame(step); else { clearTimeout(backstop); land(); }
    }
    el.textContent = '0';
    requestAnimationFrame(step);
  }

  /* ---- 2. reveal --------------------------------------------------------- */
  var io = null;
  function onEnter(entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      var el = en.target;
      if (el.hasAttribute('data-shown')) return;
      el.setAttribute('data-shown', '');
      if (io) io.unobserve(el);
      var nums = el.querySelectorAll('[data-ss-count]');
      for (var i = 0; i < nums.length; i++) countUp(nums[i]);
      if (el.hasAttribute('data-ss-count')) countUp(el);
    });
  }

  function tag() {
    if (!io) return;
    RISE.forEach(function (sel) {
      var els = document.querySelectorAll(sel);
      var lastParent = null;
      var i = 0;
      for (var k = 0; k < els.length; k++) {
        var el = els[k];
        if (el.hasAttribute('data-ss-rise')) continue;
        if (el.closest('.fuel, dialog')) continue;
        if (el.parentNode !== lastParent) { lastParent = el.parentNode; i = 0; }
        el.setAttribute('data-ss-rise', '');
        el.style.setProperty('--rise-delay', (Math.min(i, MAX_STEPS) * STEP) + 'ms');
        i += 1;
        io.observe(el);
        forceLater(el);
      }
    });
    /* proof numbers outside a tagged parent still count once seen */
    var loose = document.querySelectorAll('[data-ss-count]:not([data-counted])');
    for (var j = 0; j < loose.length; j++) {
      if (!loose[j].closest('[data-ss-rise]')) io.observe(loose[j]);
    }
  }

  /* ---- 2b. viewport backstop ----------------------------------------------
     Seen 2026-09-02 on the Spicy South PDP: the proof row sat in view with
     the observer never reporting it (the details column lives inside the
     theme's own layout containers). Anything tagged but unshown that is
     inside the viewport by plain geometry is shown on the next tick and
     on every scroll, so a failed observation costs a beat, never content. */
  /* v2 (2026-09-03): the backstop is no longer optional. Every tagged
     element is force-shown FORCE_MS after tagging whether or not it has been
     scrolled to, and the geometry sweep runs on the next frame of every
     scroll instead of 160ms later. Content can be late by a beat; it can
     never be missing. */
  function forceLater(el) {
    setTimeout(function () {
      if (!el.hasAttribute('data-shown')) onEnter([{ isIntersecting: true, target: el }]);
    }, FORCE_MS);
  }
  var sweepT = null;
  var sweepRaf = 0;
  function sweepNow() {
    if (sweepRaf) return;
    sweepRaf = requestAnimationFrame(function () { sweepRaf = 0; sweep(); });
  }
  function sweep() {
    sweepT = null;
    var els = document.querySelectorAll('[data-ss-rise]:not([data-shown])');
    var h = window.innerHeight || document.documentElement.clientHeight;
    for (var i = 0; i < els.length; i++) {
      var r = els[i].getBoundingClientRect();
      if (r.bottom > 0 && r.top < h && (r.width || r.height)) onEnter([{ isIntersecting: true, target: els[i] }]);
    }
  }
  function scheduleSweep(ms) {
    if (sweepT) return;
    sweepT = setTimeout(sweep, ms || 160);
  }

  /* ---- 4. ripple --------------------------------------------------------- */
  function ripple(e) {
    if (reduce) return;
    var host = e.target && e.target.closest ? e.target.closest(RIPPLE) : null;
    if (!host) return;
    var r = host.getBoundingClientRect();
    if (!r.width) return;
    var s = document.createElement('span');
    s.className = 'ss-ripple';
    s.setAttribute('aria-hidden', 'true');
    s.style.setProperty('--rx', Math.round(e.clientX - r.left) + 'px');
    s.style.setProperty('--ry', Math.round(e.clientY - r.top) + 'px');
    s.style.setProperty('--rs', String(Math.ceil(Math.max(r.width, r.height) / 5)));
    host.appendChild(s);
    var gone = false;
    function rm() { if (gone) return; gone = true; if (s.parentNode) s.parentNode.removeChild(s); }
    s.addEventListener('animationend', rm);
    setTimeout(rm, 800);
  }

  /* ---- 5. cart ----------------------------------------------------------- */
  function replay(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    setTimeout(function () { el.classList.remove(cls); }, 700);
  }
  function onCart() {
    var icons = document.querySelectorAll('.header-actions__cart-icon');
    for (var i = 0; i < icons.length; i++) replay(icons[i], 'ss-bump');
    if (!reduce && window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
      try { if (navigator.vibrate) navigator.vibrate(12); } catch (e) {}
    }
  }

  /* ---- boot -------------------------------------------------------------- */
  function boot() {
    var body = document.body;
    if (!body) return;
    document.addEventListener('pointerdown', ripple, { passive: true });
    document.addEventListener('cart:update', onCart);

    if (reduce || !('IntersectionObserver' in window)) {
      /* numbers land on their final value, nothing is hidden */
      var nums = document.querySelectorAll('[data-ss-count]');
      for (var i = 0; i < nums.length; i++) nums[i].textContent = nums[i].getAttribute('data-ss-count');
      return;
    }
    body.classList.add('ss-motion');
    io = new IntersectionObserver(onEnter, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    tag();
    scheduleSweep(300);
    window.addEventListener('scroll', sweepNow, { passive: true });
    window.addEventListener('resize', sweepNow, { passive: true });
    window.addEventListener('pageshow', sweepNow);

    /* Savor morphs sections in place on navigation and filter changes:
       re-tag whatever arrives, debounced. */
    var main = document.getElementById('MainContent');
    if (main && 'MutationObserver' in window) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () { pending = null; tag(); scheduleSweep(500); }, 160);
      }).observe(main, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:section:load', function () { setTimeout(tag, 50); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
